import {
  differenceInDays,
  differenceInMinutes,
  format,
  isToday,
  isTomorrow,
  roundToNearestMinutes
} from 'date-fns'
import { parseTimezoneISO } from 'shop/utils'
import {
  PartnerAddress,
  PartnerStore,
  PartnerStoreBrowse,
  StoreRankingCriteria,
  StoreRankReasons
} from '../types'
import { TagItems } from './StoreTags'

/**
 * Takes Partner Address and returns readable one line address
 */
export const formatPartnerAddressToString = (address: PartnerAddress) => {
  const { lineOne, lineTwo, city, zip } = address
  let formattedAddress = ''
  if (lineOne) formattedAddress += `${lineOne}, `
  if (lineTwo) formattedAddress += `${lineTwo}, `
  if (city) formattedAddress += `${city}, `
  if (zip) formattedAddress += `${zip}, `
  formattedAddress = formattedAddress.replace(/ {2,}/g, ' ') // remove any double white spaces
  formattedAddress = formattedAddress.trim() // remove trailing whitespace
  formattedAddress = formattedAddress.replace(/,$/, '') // remove any trailing commas

  return formattedAddress
}

/**
 * Format the earliest slot to be displayed appropriately.
 * Timezone is set to UK ('Europe/London')
 */
export const formatEarliestSlot = (
  earliestSlot: {
    from: string
    to: string
  } | null,
  currentDateTime: Date
) => {
  if (!earliestSlot) return `Currently unavailable`
  const earliestSlotFrom = parseTimezoneISO(earliestSlot.from)
  const currentDateTimeMins = roundToNearestMinutes(currentDateTime)
  const earliestSlotFromMins = roundToNearestMinutes(earliestSlotFrom)

  if (differenceInMinutes(earliestSlotFromMins, currentDateTimeMins) < 60) {
    const earliestSlotTo = parseTimezoneISO(earliestSlot.to)
    const earliestSlotToMins = roundToNearestMinutes(earliestSlotTo)
    const mins = {
      from: differenceInMinutes(earliestSlotFromMins, currentDateTimeMins),
      to: differenceInMinutes(earliestSlotToMins, currentDateTimeMins)
    }
    return `Today in ${mins.from} - ${mins.to} mins`
  }
  if (
    differenceInMinutes(earliestSlotFromMins, currentDateTimeMins) >= 60 &&
    isToday(earliestSlotFrom)
  ) {
    return `Today from ${format(earliestSlotFromMins, 'HH:mm')}`
  }

  if (isTomorrow(earliestSlotFrom)) {
    return `Tomorrow from ${format(earliestSlotFrom, 'HH:mm')}`
  }

  if (differenceInDays(earliestSlotFrom, currentDateTime) <= 7) {
    return `${format(earliestSlotFrom, 'EEEE')} from ${format(earliestSlotFrom, 'HH:mm')}`
  }

  return `${format(earliestSlotFrom, 'E, do LLL')} from ${format(earliestSlotFrom, 'HH:mm')}`
}

/** Filter a list of stores by the store's name and postcode */
export const filterStores = (input: string, stores: PartnerStoreBrowse[]) => {
  const matchedStores = stores.filter(({ store }) => {
    const inputToCompare = input.toLowerCase()

    const storeName = store.name.toLowerCase()
    const storePostcode = store.address?.zip?.toLowerCase() || ''

    return (
      storeName.includes(inputToCompare) ||
      storePostcode.includes(inputToCompare)
    )
  })
  return matchedStores
}

type GetStoreTagContentProps = {
  tag: TagItems
  isDeliveryEnabled: boolean
  isPickupEnabled: boolean
  hasLocation: boolean
  sortedRankReasons: StoreRankReasons[]
  sortCriteria: StoreRankingCriteria
}
/**
 * Given user preferences and Store availability, calculate reasoning for Store Tag logic.
 */
export const getStoreTagContent = ({
  tag,
  isDeliveryEnabled,
  isPickupEnabled,
  hasLocation,
  sortedRankReasons,
  sortCriteria
}: GetStoreTagContentProps) => {
  switch (tag) {
    case 'DELIVERY': {
      if (!isDeliveryEnabled) {
        return {
          isEnabled: false,
          text: 'Delivery unavailable'
        }
      }
      if (hasLocation) {
        if (sortedRankReasons.includes('DELIVERY_WITHIN_RANGE')) {
          return {
            isEnabled: true,
            text: 'Delivers to you'
          }
        }
        if (sortedRankReasons.includes('DELIVERY_ACCEPTED')) {
          return {
            isEnabled: false,
            text: 'Outside delivery area'
          }
        }
      }
      if (sortedRankReasons.includes('DELIVERY_ACCEPTED')) {
        return {
          isEnabled: true,
          text: 'Delivery available'
        }
      }
      return {
        isEnabled: false,
        text: 'Delivery unavailable'
      }
    }
    case 'PICKUP': {
      if (!isPickupEnabled) {
        return {
          isEnabled: false,
          text: 'Pickup unavailable'
        }
      }
      if (hasLocation) {
        if (sortedRankReasons.includes('PICKUP_WITHIN_RANGE')) {
          return {
            isEnabled: true,
            text: 'Pickup near you'
          }
        }
        if (sortedRankReasons.includes('PICKUP_ACCEPTED')) {
          return {
            isEnabled: false,
            text: "Location isn't nearby"
          }
        }
      }
      if (sortedRankReasons.includes('PICKUP_ACCEPTED')) {
        return {
          isEnabled: true,
          text: 'Pickup available'
        }
      }
      return {
        isEnabled: false,
        text: 'Pickup unavailable'
      }
    }
    case 'TIME': {
      if (
        (isDeliveryEnabled &&
          sortedRankReasons.includes('DELIVERY_ORDER_DATE_AVAILABLE')) ||
        (isPickupEnabled &&
          sortedRankReasons.includes('PICKUP_ORDER_DATE_AVAILABLE'))
      ) {
        return {
          isEnabled: true,
          text: 'Date available'
        }
      }
      if (
        (isDeliveryEnabled &&
          sortedRankReasons.includes('DELIVERY_ORDER_DATETIME_AVAILABLE')) ||
        (isPickupEnabled &&
          sortedRankReasons.includes('PICKUP_ORDER_DATETIME_AVAILABLE'))
      ) {
        return {
          isEnabled: true,
          text: 'Time available'
        }
      }
      return {
        isEnabled: false,
        text: !!sortCriteria.fulfillmentTime
          ? 'Time unavailable'
          : 'Date unavailable'
      }
    }
    default: {
      return {
        isEnabled: false,
        text: null
      }
    }
  }
}

/** Scrolls the page and LocationSelectModal to the appropriate currentlyViewedStore */
export const handleScrollToStore = (
  itemRefs: React.MutableRefObject<Record<string, HTMLLIElement | null>>,
  currentlyViewedStore: PartnerStore
) => {
  const storeListItemEl = itemRefs.current[currentlyViewedStore.slug]
  const modalStoreListItemEl =
    itemRefs.current[`${currentlyViewedStore.slug}-modal`]
  const locationSelectModalEl = document.getElementById('location-select-modal')
  if (storeListItemEl) {
    // scroll to and expand store content on Landing page
    const rect = storeListItemEl.getBoundingClientRect()
    const windowScrollTop = window.scrollY
    const windowTop = rect.top + windowScrollTop - 250

    window.scrollTo({
      top: windowTop,
      behavior: 'smooth'
    })
  }
  if (locationSelectModalEl && modalStoreListItemEl) {
    // scroll to and expand store content on LocationSelectModal
    const rect = modalStoreListItemEl.getBoundingClientRect()
    const modalScrollTop = locationSelectModalEl.scrollTop
    const modalTop = rect.top + modalScrollTop - 250
    locationSelectModalEl.scrollTo({
      top: modalTop,
      behavior: 'smooth'
    })
  }
}
